import { SnackbarProvider } from 'notistack';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider } from '../auth/context/jwt/auth-provider';
import { MotionLazy } from '../components/animate/MotionLazy';
import { ProgressBar } from '../components/progress-bar/ProgressBar';
import { defaultSettings as genericDefaultSettings } from '../components/settings/config-settings';
import { SettingsProvider } from '../components/settings/context/settings-provider';
import { SettingsDrawer } from '../components/settings/drawer/SettingsDrawer';
import type { SettingsState } from '../components/settings/types';
import { Snackbar } from '../components/snackbar/snackbar';
import '../global.css';
import { I18nProvider } from '../locales/i18n-provider';
import { themeConfig } from '../theme/theme-config';
import { ThemeProvider, type ThemeProviderProps } from '../theme/theme-provider';

type AppProviderProps = {
    children: React.ReactNode;
    /**
     * Custom ThemeProvider component type. Use this when implementing a custom theme for your app.
     */
    themeProvider?: React.ComponentType<ThemeProviderProps>;
    defaultSettings?: SettingsState;
};

/**
 * This component is a wrapper for all the providers that you need to use in your app.
 * Add your own routing as a child of this component.
 *
 * 1. {@link MotionLazyContainer}
 * 2. {@link ThemeProvider}
 * 3. {@link ThemeSettings}
 * 4. {@link ThemeLocalization}
 * 5. {@link SnackbarProvider}
 *
 * @param param {@link AppProviderProps}
 *
 * ## Usage
 * ```tsx
 *
 * import { AppProvider } from '@mej/ui';
 * return (
 *     <AppProvider>
 *         Hello World
 *     </AppProvider>
 * );
 * ```
 */
export function AppProvider({
    children,
    themeProvider: ThemeProviderComponent = ThemeProvider,
    defaultSettings = genericDefaultSettings,
}: AppProviderProps) {
    return (
        <HelmetProvider>
            <I18nProvider>
                <AuthProvider>
                    <SettingsProvider defaultSettings={defaultSettings}>
                        <ThemeProviderComponent
                            modeStorageKey={themeConfig.modeStorageKey}
                            defaultMode={
                                themeConfig.enableSystemMode ? 'system' : themeConfig.defaultMode
                            }
                        >
                            <MotionLazy>
                                <Snackbar />
                                <ProgressBar />
                                <SettingsDrawer defaultSettings={defaultSettings} />
                                <SnackbarProvider>{children}</SnackbarProvider>
                            </MotionLazy>
                        </ThemeProviderComponent>
                    </SettingsProvider>
                </AuthProvider>
            </I18nProvider>
        </HelmetProvider>
    );
}
